import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NotificationAlertService {
  private audioContext: AudioContext | null = null;
  private alertTimer: number | null = null;
  private activeNotifications = 0;
  private audioBlocked = false;
  private activeMessageKeys = new Set<string>();
  private contextWarmed = false;
  private pollDelayTimer: number | null = null;
  private pollTimer: number | null = null;
  private hasConfirmedVisibleNotification = false;
  private pollCycleCount = 0;

  /**
   * HTML <audio> element used as the primary alert sound source.  Unlike
   * AudioContext, an HTMLMediaElement that has been .play()-ed once under a
   * user gesture can be replayed programmatically thereafter without
   * requiring another user activation.  This works around the browser
   * autoplay policy that blocks the first notification's sound.
   */
  private alertAudioEl: HTMLAudioElement | null = null;
  private audioUnlocked = false;

  /**
   * Emits true when a notification arrived but the browser's autoplay
   * policy is blocking the alert sound until the user interacts with the
   * page.  The UI can subscribe to this to show a "tap to enable sound"
   * prompt so the very first notification is not silently missed.
   */
  readonly soundBlocked$ = new BehaviorSubject<boolean>(false);

  constructor(private ngZone: NgZone) {
    this.attachUnlockHandlers();
  }

  notifyIncoming(messageKey?: string): void {
    if (messageKey) {
      if (this.activeMessageKeys.has(messageKey)) {
        return;
      }

      this.activeMessageKeys.add(messageKey);
    }

    this.activeNotifications += 1;
    if (this.activeNotifications === 1) {
      this.hasConfirmedVisibleNotification = false;
    }
    this.startNotificationPolling();
    void this.startAlertLoop();
  }

  notifyResolved(messageKey?: string): void {
    if (messageKey && this.activeMessageKeys.has(messageKey)) {
      this.activeMessageKeys.delete(messageKey);
    } else if (messageKey) {
      // The close/click event carried a key we do not track (e.g. key built
      // differently by the SW or a stale notification).  Treat it as resolved
      // anyway — otherwise the alert loop would never stop.
      console.warn('Resolving notification alert for untracked key:', messageKey);
    }

    this.activeNotifications = Math.max(0, this.activeNotifications - 1);
    if (this.activeNotifications === 0) {
      this.reset();
    }
  }

  reset(): void {
    this.activeNotifications = 0;
    this.audioBlocked = false;
    this.hasConfirmedVisibleNotification = false;
    this.pollCycleCount = 0;
    this.activeMessageKeys.clear();
    this.stopAlertLoop();
    this.stopNotificationPolling();
  }

  /**
   * Explicitly unlock the alert sound.  Call from a "Enable sound" UI affordance
   * or any user-initiated handler.  Resumes the AudioContext under a user
   * activation so subsequent notifications play instantly.
   */
  unlockSound(): void {
    this.warmUpContext();
  }

  /**
   * Path to the alert bell sound served from /public.  Using a real file
   * (instead of a synthesized Web Audio tone) lets an HTML <audio> element
   * be unlocked once under a user gesture and replayed on every subsequent
   * notification without re-activation — working around the browser
   * autoplay policy that blocks the first notification's sound.
   */
  private static readonly ALERT_SOUND_URL = 'sounds/alert-bell.wav';

  private getAlertAudioEl(): HTMLAudioElement | null {
    if (typeof document === 'undefined') {
      return null;
    }

    if (!this.alertAudioEl) {
      const el = document.createElement('audio');
      el.src = NotificationAlertService.ALERT_SOUND_URL;
      el.preload = 'auto';
      el.muted = false;
      // Keep it ready without occupying layout.
      el.style.display = 'none';
      this.alertAudioEl = el;
    }

    return this.alertAudioEl;
  }

  /**
   * Unlock the HTML <audio> element under a user gesture.  Once .play()
   * succeeds here, the element can be replayed later without activation.
   */
  private unlockAudioElement(): void {
    if (this.audioUnlocked) {
      return;
    }

    const el = this.getAlertAudioEl();
    if (!el) {
      return;
    }

    // Mute for the unlock play so the user does not hear a stray sound.
    const previousMuted = el.muted;
    el.muted = true;
    el.currentTime = 0;

    const playPromise = el.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => {
          el.pause();
          el.muted = previousMuted;
          this.audioUnlocked = true;
          this.audioBlocked = false;
          this.soundBlocked$.next(false);
        })
        .catch(() => {
          el.muted = previousMuted;
        });
    } else {
      el.muted = previousMuted;
    }
  }

  private attachUnlockHandlers(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const unlock = () => {
      // Resume synchronously inside the gesture handler — some browsers
      // only honor resume() when called within the active user-activation
      // window.  Do NOT defer to a promise microtask.
      this.warmUpContext();
      // If a notification is already pending and was blocked, start it now
      // while we still hold the user activation.
      if (this.activeNotifications > 0 && this.alertTimer === null) {
        void this.startAlertLoop();
      }
    };

    // Capture the earliest possible user gestures.  pointerover/mousemove
    // fire before click and count as activation in modern browsers, so the
    // context is unlocked as soon as the user moves the mouse over the app.
    window.addEventListener('pointerover', unlock, { passive: true });
    window.addEventListener('pointerdown', unlock, { passive: true });
    window.addEventListener('mousedown', unlock, { passive: true });
    window.addEventListener('mouseover', unlock, { passive: true });
    window.addEventListener('click', unlock, { passive: true });
    window.addEventListener('keydown', unlock, { passive: true });
    window.addEventListener('touchstart', unlock, { passive: true });
    window.addEventListener('focus', unlock);
    window.addEventListener('pageshow', unlock);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.warmUpContext();
        if (this.activeNotifications > 0 && this.alertTimer === null) {
          void this.startAlertLoop();
        }
      }
    });
  }

  /**
   * Create and resume the AudioContext on/after a user gesture so it is
   * ready before any notification arrives.
   *
   * Browsers suspend AudioContext until the page receives a user activation.
   * We must therefore resume the context on EVERY gesture (not just the
   * first), because the context can be re-suspended by the browser when the
   * tab is backgrounded or after long inactivity.  Once resumed, the context
   * stays "running" and subsequent notifications play sound instantly with
   * no further interaction.
   */
  private warmUpContext(): void {
    // Unlock the HTML <audio> element first — this is the most reliable
    // cross-browser way to satisfy the autoplay policy.
    this.unlockAudioElement();

    const ctx = this.getAudioContext();
    if (!ctx) {
      return;
    }

    if (ctx.state === 'running') {
      this.contextWarmed = true;
      this.audioBlocked = false;
      this.soundBlocked$.next(false);
      return;
    }

    // Resume synchronously inside the user-gesture handler.  Some browsers
    // only honor resume() while a user activation is active.
    try {
      ctx.resume();
    } catch {
      /* ignore */
    }

    // Schedule a silent buffer immediately.  On several browsers the
    // AudioContext is only considered "unlocked" after a buffer has been
    // dispatched to the destination under a user gesture, not merely after
    // resume() resolves.
    try {
      this.playSilentUnlockBuffer(ctx);
    } catch {
      /* ignore */
    }

    // Also wait for the resume() promise in case the state flips async.
    ctx.resume()
      .then(() => {
        if (ctx.state === 'running') {
          this.contextWarmed = true;
          this.audioBlocked = false;
          this.soundBlocked$.next(false);
          if (this.activeNotifications > 0 && this.alertTimer === null) {
            void this.startAlertLoop();
          }
        }
      })
      .catch(() => {});
  }

  /**
   * Play a zero-volume buffer to satisfy browsers that require an actual
   * audio node to be scheduled under a user gesture before the context is
   * considered unlocked.
   */
  private playSilentUnlockBuffer(ctx: AudioContext): void {
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, ctx.currentTime);
    source.connect(gain);
    gain.connect(ctx.destination);
    source.start(ctx.currentTime);
  }

  private async startAlertLoop(): Promise<void> {
    if (this.alertTimer !== null || this.activeNotifications === 0) {
      return;
    }

    // The HTML <audio> element, once unlocked under a user gesture, can
    // play without further activation.  Prefer it over the AudioContext
    // (which requires resume() inside an active gesture).
    if (!this.audioUnlocked) {
      // Try to unlock the AudioContext as a fallback.  This only succeeds
      // if called within a user gesture; from a push handler it will fail.
      const audioReady = await this.resumeAudioContext();
      if (!audioReady) {
        this.audioBlocked = true;
        this.soundBlocked$.next(true);
        console.warn('Notification sound is blocked until the page receives a user interaction.');
        return;
      }
    }

    this.audioBlocked = false;
    this.soundBlocked$.next(false);

    this.playBellPattern();

    this.ngZone.runOutsideAngular(() => {
      this.alertTimer = window.setInterval(() => {
        this.playBellPattern();
      }, 1800);
    });
  }

  private stopAlertLoop(): void {
    if (this.alertTimer !== null) {
      window.clearInterval(this.alertTimer);
      this.alertTimer = null;
    }

    if (this.activeNotifications === 0) {
      this.audioBlocked = false;
    }
  }

  private async retryPendingAlert(): Promise<void> {
    if (this.activeNotifications === 0) {
      return;
    }

    if (this.alertTimer !== null && !this.audioBlocked) {
      return;
    }

    await this.startAlertLoop();
  }

  /**
   * Polls ServiceWorkerRegistration.getNotifications() to detect when all
   * notifications have been dismissed.  This is the reliable sound‑stop
   * mechanism — it does not depend on 'notificationclose' events which may
   * not fire on every platform.
   */
  private startNotificationPolling(): void {
    if (this.pollTimer !== null || this.pollDelayTimer !== null) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      // Delay the first poll by 3 s so the SW has time to call showNotification().
      this.pollDelayTimer = window.setTimeout(() => {
        this.pollDelayTimer = null;
        void this.checkNotificationsDismissed();
        this.pollTimer = window.setInterval(() => {
          void this.checkNotificationsDismissed();
        }, 1500);
      }, 3000);
    });
  }

  private stopNotificationPolling(): void {
    if (this.pollDelayTimer !== null) {
      window.clearTimeout(this.pollDelayTimer);
      this.pollDelayTimer = null;
    }
    if (this.pollTimer !== null) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private async checkNotificationsDismissed(): Promise<void> {
    if (this.activeNotifications === 0) {
      this.stopNotificationPolling();
      return;
    }

    try {
      const reg = await navigator.serviceWorker?.ready;
      if (!reg) {
        return;
      }

      const notifications = await reg.getNotifications();
      this.pollCycleCount += 1;

      if (notifications.length > 0) {
        this.hasConfirmedVisibleNotification = true;
        return;
      }

      // Stop the sound only after we have confirmed a visible notification
      // and it has since been dismissed.  We never stop the alert based on a
      // poll-cycle count fallback — that would cut the sound short when the
      // service worker has not yet called showNotification().
      if (this.hasConfirmedVisibleNotification) {
        this.reset();
      }
    } catch {
      // getNotifications() unavailable — fall back to SW message path
    }
  }

  private async resumeAudioContext(): Promise<boolean> {
    const context = this.getAudioContext();
    if (!context) {
      return false;
    }

    if (context.state === 'running') {
      return true;
    }

    try {
      await context.resume();
      return this.audioContext?.state === 'running';
    } catch (error) {
      console.warn('Unable to start notification sound.', error);
      return false;
    }
  }

  private getAudioContext(): AudioContext | null {
    if (typeof window === 'undefined') {
      return null;
    }

    if (!this.audioContext) {
      const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextCtor) {
        return null;
      }

      this.audioContext = new AudioContextCtor();
    }

    return this.audioContext;
  }

  private playBellPattern(): void {
    // Prefer the HTML <audio> element — once unlocked under a user gesture
    // it can be replayed on every notification without re-activation.
    if (this.audioUnlocked && this.playAlertAudioElement()) {
      return;
    }

    // Fallback to Web Audio API.
    const context = this.getAudioContext();
    if (!context || context.state !== 'running') {
      return;
    }

    const startTime = context.currentTime;
    this.playTone(context, startTime, 1046.5, 0.16, 0.18);
    this.playTone(context, startTime + 0.2, 1318.5, 0.18, 0.16);
    this.playTone(context, startTime + 0.42, 1568, 0.22, 0.12);
  }

  /**
   * Replay the unlocked <audio> element.  Returns true if the play was
   * initiated (i.e. the element is unlocked and ready).
   */
  private playAlertAudioElement(): boolean {
    const el = this.getAlertAudioEl();
    if (!el || !this.audioUnlocked) {
      return false;
    }

    try {
      el.muted = false;
      el.currentTime = 0;
      const playPromise = el.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // If the element got re-locked (e.g. after long backgrounding),
          // mark it as locked so the next gesture re-unlocks it.
          this.audioUnlocked = false;
        });
      }
      return true;
    } catch {
      this.audioUnlocked = false;
      return false;
    }
  }

  private playTone(
    context: AudioContext,
    startTime: number,
    frequency: number,
    duration: number,
    peakGain: number
  ): void {
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(frequency, startTime);
    oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.98, startTime + duration);

    gainNode.gain.setValueAtTime(0.0001, startTime);
    gainNode.gain.exponentialRampToValueAtTime(peakGain, startTime + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);

    oscillator.start(startTime);
    oscillator.stop(startTime + duration + 0.03);
  }
}